import React from "react"
import { Box, Stack } from "@mui/material"
import { ViraButton } from "../atoms/ViraButton"
import { ContentCard } from "../molecules/ContentCard"
import { SectionHeader } from "../molecules/SectionHeader"
import { ProgressNoteFieldRow } from "../molecules/ProgressNoteFieldRow"

export type ProgressNoteSection = {
  id: string
  heading: string
  body: string
}

export type ProgressNoteCardProps = {
  clientName: string
  title?: string
  sections?: ProgressNoteSection[]
  /**
   * Standalone Storybook / non-Loop: page chrome with eyebrow.
   * Under Client Detail `ClientDetailPage`, pass `false` and use a card title.
   */
  showPageHeader?: boolean
  onCopy?: () => void
  onEdit?: () => void
  onApprove?: () => void
}

const DEFAULT_SECTIONS: ProgressNoteSection[] = [
  {
    id: "subjective",
    heading: "Subjective",
    body: "Client reported improved sleep onset this week and continued urges after work, though intensity felt lower.",
  },
  {
    id: "objective",
    heading: "Objective",
    body: "Diary card completion 5/7 days. PHQ-9 trending down. Practiced urge surfing in session with good engagement.",
  },
  {
    id: "assessment",
    heading: "Assessment",
    body: "Progressing with distress tolerance skills; interpersonal effectiveness remains a growth area.",
  },
  {
    id: "plan",
    heading: "Plan",
    body: "Continue urge surfing nightly; add one opposite-action practice; review next session.",
  },
]

export function ProgressNoteCard({
  clientName,
  title = "Progress note",
  sections = DEFAULT_SECTIONS,
  showPageHeader = true,
  onCopy,
  onEdit,
  onApprove,
}: ProgressNoteCardProps) {
  const actions = (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1}
      useFlexGap
      sx={{
        width: { xs: "100%", sm: "auto" },
        "& > *": { width: { xs: "100%", sm: "auto" } },
      }}
    >
      <ViraButton variant="secondary" onClick={onCopy} sx={{ minHeight: 40 }}>
        Copy
      </ViraButton>
      <ViraButton variant="secondary" onClick={onEdit} sx={{ minHeight: 40 }}>
        Edit
      </ViraButton>
      <ViraButton onClick={onApprove} sx={{ minHeight: 40 }}>
        Approve note
      </ViraButton>
    </Stack>
  )

  return (
    <Box
      sx={{
        maxWidth: showPageHeader ? { xs: "100%", lg: 1028 } : "100%",
        width: "100%",
      }}
    >
      {showPageHeader ? (
        <SectionHeader
          level="page"
          eyebrow={`${clientName} · After the session`}
          title={title}
          actions={actions}
        />
      ) : null}

      <ContentCard
        title={showPageHeader ? undefined : title}
        actions={showPageHeader ? undefined : actions}
        sx={{ maxWidth: "100%" }}
      >
        <Stack spacing={3}>
          {sections.map((section) => (
            <ProgressNoteFieldRow
              key={section.id}
              heading={section.heading}
              body={section.body}
            />
          ))}
        </Stack>
      </ContentCard>
    </Box>
  )
}
